import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Stack } from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import ProgressObservations from 'components/progress/physical/ProgressObservations';
import ClientPlan from 'components/client/overview-profile/ClientPlan';
import ClientTabs from 'components/client/overview-profile/ClientTabs';
import CreatePerformanceButton from 'components/performance/CreatePerformanceButton';
import GoBackButton from 'components/shared/buttons/GoBackButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import OverviewProfileBasicDetails from 'components/shared/overview-profile/OverviewProfileBasicDetails';
import { clientViewFields } from 'common/constants/query-fields/client';
import requestStatus from 'common/constants/status';
import { isAdmin } from 'common/utils/user';
import { roles } from 'common/constants/options';
import AppBarContext from 'context/AppBarContext';
import clientSlice from 'store/modules/client';
import routes from 'common/routes';

function ClientOverviewProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['labels', 'routes', 'buttons']);
  const pageTitle = t('client.view', { ns: 'routes' });
  const { id } = useParams();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const client = useSelector((state) => state.client.data.viewClient);
  const role = useSelector((state) => state.auth.user.role);
  const status = useSelector((state) => state.client.status.view);

  const isAdminUser = isAdmin(role);
  // El entrenador y el admin pueden registrar rendimientos; el supervisor solo consulta.
  const canCreatePerformance = role === roles.administrator || role === roles.trainer;
  const isLoadingClient =
    status === requestStatus.loading || status === requestStatus.idle || !client;

  // Recarga al cambiar de :id (antes con deps [] mostraba el cliente ANTERIOR — bug "último cliente").
  useEffect(() => {
    dispatch(clientSlice.actions.view({ fields: clientViewFields, id }));
  }, [dispatch, id]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(null);
  }, [setAppBarButton, setAppBarTitle]);

  const handleViewPerformance = () => {
    navigate(routes.viewClientPerformance.path.replace(':id', id));
  };

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          // Sin `px` propio: la cabecera se alinea con el padding lateral de la sección, igual
          // que la ficha y los mosaicos de abajo.
          pt: 1,
          mb: { xs: 1.5, md: 2 },
          gap: 1
        }}>
        <GoBackButton />
        {/* A 375px las dos acciones no caben junto al "volver": pasan a columna a ancho completo. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems="stretch"
          sx={{
            flex: { xs: '1 1 100%', sm: '0 1 auto' },
            justifyContent: 'flex-end',
            // El botón de crear puede venir envuelto en el span del tooltip (cliente inactivo),
            // así que se estira tanto el hijo directo como el botón que lleva dentro.
            '& > *': { width: { xs: '100%', sm: 'auto' } },
            '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } }
          }}>
          {canCreatePerformance && !isLoadingClient && client?.id && (
            <CreatePerformanceButton client={client} />
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<BarChartIcon />}
            onClick={handleViewPerformance}
            sx={{
              color: 'gold.main',
              borderColor: 'gold.main',
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)',
              // `gold.dark` y no `gold.bright`: en modo claro el dorado brillante se pierde.
              '&:hover': { borderColor: 'gold.dark', bgcolor: 'gold.soft' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            {t('viewPerformance', { ns: 'buttons', defaultValue: 'Ver rendimiento' })}
          </Button>
        </Stack>
      </Box>
      <OverviewProfileBasicDetails user={client} loading={isLoadingClient} />
      {isAdminUser && <ClientPlan client={client} loading={isLoadingClient} />}
      <ClientTabs client={client} loading={isLoadingClient} />
      {client?.id && <ProgressObservations clientId={client.id} />}
    </>
  );
}

export default ClientOverviewProfile;
